import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Button,
  Divider,
  IconButton,
  Avatar,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { CSVLink } from "react-csv";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import PersonIcon from "@mui/icons-material/Person";
import EmptyState from "./EmptyState";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "85%",
  maxWidth: 960,
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
  p: 0,
  maxHeight: "90vh",
  overflowY: "auto",
};

function UserDetailModal({ open, onClose, userData, loading }) {
  const [selectedPeriod, setSelectedPeriod] = useState("monthly");

  const handlePeriodChange = (event, newValue) => {
    setSelectedPeriod(newValue);
  };

  const getChartAndDownloadData = (location) => {
    let chartData = [];
    if (!location) return { chartData: [], flattenedData: [], headers: [] };

    if (selectedPeriod === "monthly") chartData = location.monthlyTrend || [];
    if (selectedPeriod === "weekly") chartData = location.weeklyTrend || [];
    if (selectedPeriod === "daily") chartData = location.dailyTrend || [];

    const flattenedData = chartData.map((d) => ({
      period: d.period,
      pet: d.weights?.pet ?? 0,
      aluminum: d.weights?.aluminum ?? 0,
      mixed: d.weights?.mixed ?? 0,
      paper: d.weights?.paper ?? 0,
      total: d.weights?.total ?? 0,
    }));

    const headers = [
      { label: "기간", key: "period" },
      { label: "페트(kg)", key: "pet" },
      { label: "알루미늄(kg)", key: "aluminum" },
      { label: "혼합(kg)", key: "mixed" },
      { label: "파지(kg)", key: "paper" },
      { label: "총합(kg)", key: "total" },
    ];

    return { chartData, flattenedData, headers };
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 300,
            }}
          >
            <CircularProgress />
          </Box>
        ) : !userData ? (
          <Box sx={{ p: 4 }}>
            <EmptyState message="데이터를 불러올 수 없습니다." icon={PersonIcon} />
          </Box>
        ) : (
          <>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 4,
                py: 3,
                background: "linear-gradient(135deg, #2a9ac6 0%, #1d7396 100%)",
                color: "white",
                borderRadius: "16px 16px 0 0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: "rgba(255,255,255,0.2)",
                    fontSize: "1.3rem",
                    fontWeight: 700,
                  }}
                >
                  {userData.userName?.[0] || "?"}
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: "white" }}>
                    {userData.userName} 님의 상세 지표
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    {userData.locations?.length || 0}개 수거 장소
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={onClose} sx={{ color: "white" }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Period Tabs */}
            <Box sx={{ px: 4, pt: 3 }}>
              <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tabs value={selectedPeriod} onChange={handlePeriodChange}>
                  <Tab label="월별" value="monthly" />
                  <Tab label="주별" value="weekly" />
                  <Tab label="일별" value="daily" />
                </Tabs>
              </Box>
            </Box>

            {/* Location Charts */}
            <Box sx={{ px: 4, pb: 4 }}>
              {userData.locations && userData.locations.length > 0 ? (
                userData.locations.map((location) => {
                  const { chartData, flattenedData, headers } =
                    getChartAndDownloadData(location);

                  return (
                    <Box key={location.locationId} sx={{ mb: 4 }}>
                      <Divider sx={{ mb: 3 }} />
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {location.locationName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {location.address}
                          </Typography>
                        </Box>
                        {flattenedData.length > 0 && (
                          <CSVLink
                            data={flattenedData}
                            headers={headers}
                            filename={`${userData.userName}_${location.locationName}_${selectedPeriod}.csv`}
                            style={{ textDecoration: "none" }}
                          >
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<DownloadIcon />}
                            >
                              CSV 다운로드
                            </Button>
                          </CSVLink>
                        )}
                      </Box>

                      <Box sx={{ height: 300 }}>
                        {chartData.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData}>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#f0f0f0"
                              />
                              <XAxis
                                dataKey="period"
                                tick={{ fontSize: 12 }}
                              />
                              <YAxis
                                tick={{ fontSize: 12 }}
                                label={{
                                  value: "kg",
                                  angle: -90,
                                  position: "insideLeft",
                                }}
                              />
                              <Tooltip />
                              <Legend />
                              <Bar
                                dataKey="weights.pet"
                                name="페트"
                                stackId="a"
                                fill="#8884d8"
                              />
                              <Bar
                                dataKey="weights.aluminum"
                                name="알루미늄"
                                stackId="a"
                                fill="#82ca9d"
                              />
                              <Bar
                                dataKey="weights.mixed"
                                name="혼합"
                                stackId="a"
                                fill="#ffc658"
                              />
                              <Bar
                                dataKey="weights.paper"
                                name="파지"
                                stackId="a"
                                fill="#8B4513"
                                radius={[4, 4, 0, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              height: "100%",
                            }}
                          >
                            <Typography color="text.secondary">
                              표시할 데이터가 없습니다.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })
              ) : (
                <EmptyState message="등록된 수거 장소가 없습니다." />
              )}
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
}

export default UserDetailModal;
